/**
 * The §18 commissary contract (ADR-0015), pinned in one file.
 *
 * Read directly off `/tmp/integrate` (`integrate/wave-a`, backend green) —
 * `src/modules/central-kitchen/{stock-transfers,production-orders,
 * requisitions,replenishment}.controller.ts` and their `dto/*`. Every shape
 * below is copied from there, not guessed.
 *
 * THE CORRECTNESS IDEA (ADR-0015 §3): stock in transit belongs to nobody and
 * must be accounted for. A transfer is one ledger movement — decrement, then
 * in-transit, then increment — never a silent two-instant teleport, so this
 * screen makes IN_TRANSIT a real, visible state rather than a moment it skips
 * over. And a receive discrepancy (ADR-0015 §4) resolves the difference
 * explicitly, with a typed reason, never absorbs it — the resolve action is
 * the ONLY step in a transfer's life allowed to change chain-wide stock, and
 * it is presented that way.
 */

export const StockTransferStatus = {
  DRAFT: 'DRAFT',
  IN_TRANSIT: 'IN_TRANSIT',
  RECEIVED: 'RECEIVED',
  DISCREPANT: 'DISCREPANT',
  RECONCILED: 'RECONCILED',
  CANCELLED: 'CANCELLED',
} as const;
export type StockTransferStatus = (typeof StockTransferStatus)[keyof typeof StockTransferStatus];

export const TransferDiscrepancyResolution = {
  WRITTEN_OFF: 'WRITTEN_OFF',
  RETURNED_TO_ORIGIN: 'RETURNED_TO_ORIGIN',
  ACCEPTED_AS_FOUND: 'ACCEPTED_AS_FOUND',
} as const;
export type TransferDiscrepancyResolution =
  (typeof TransferDiscrepancyResolution)[keyof typeof TransferDiscrepancyResolution];

export const ProductionOrderStatus = {
  DRAFT: 'DRAFT',
  RELEASED: 'RELEASED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
} as const;
export type ProductionOrderStatus = (typeof ProductionOrderStatus)[keyof typeof ProductionOrderStatus];

export const BranchRequisitionStatus = {
  DRAFT: 'DRAFT',
  SUBMITTED: 'SUBMITTED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  FULFILLED: 'FULFILLED',
  CANCELLED: 'CANCELLED',
} as const;
export type BranchRequisitionStatus =
  (typeof BranchRequisitionStatus)[keyof typeof BranchRequisitionStatus];

// ---------------------------------------------------------------------------
// routes
// ---------------------------------------------------------------------------

export const CK_ROUTES = {
  transfers: '/stock-transfers',
  transfer: (id: string) => `/stock-transfers/${id}`,
  transferInTransit: '/stock-transfers/in-transit',
  transferDispatch: (id: string) => `/stock-transfers/${id}/dispatch`,
  transferReceive: (id: string) => `/stock-transfers/${id}/receive`,
  transferResolve: (id: string) => `/stock-transfers/${id}/resolve`,
  transferCancel: (id: string) => `/stock-transfers/${id}/cancel`,

  productionOrders: '/production-orders',
  productionOrder: (id: string) => `/production-orders/${id}`,
  productionRelease: (id: string) => `/production-orders/${id}/release`,
  productionComplete: (id: string) => `/production-orders/${id}/complete`,
  productionCancel: (id: string) => `/production-orders/${id}/cancel`,

  requisitions: '/requisitions',
  requisition: (id: string) => `/requisitions/${id}`,
  requisitionOutstanding: (id: string) => `/requisitions/${id}/outstanding`,
  requisitionSubmit: (id: string) => `/requisitions/${id}/submit`,
  requisitionApprove: (id: string) => `/requisitions/${id}/approve`,
  requisitionReject: (id: string) => `/requisitions/${id}/reject`,
  requisitionFulfil: (id: string) => `/requisitions/${id}/fulfil`,
  requisitionCancel: (id: string) => `/requisitions/${id}/cancel`,

  pars: '/replenishment/pars',
  par: (id: string) => `/replenishment/pars/${id}`,
  plan: '/replenishment/plan',
  demand: '/replenishment/demand',
  raisePurchaseOrder: '/replenishment/demand/purchase-order',

  warehouses: '/inventory/warehouses',
  items: '/inventory/items',
} as const;

/** Mirrors `permission-catalogue.json` — every key below is registered and seeded into at least one role. */
export const CK_PERMISSIONS = {
  transfersView: 'stock_transfers.view',
  transfersCreate: 'stock_transfers.create',
  transfersDispatch: 'stock_transfers.dispatch',
  transfersReceive: 'stock_transfers.receive',
  transfersResolve: 'stock_transfers.resolve',
  transfersCancel: 'stock_transfers.cancel',

  productionView: 'production.view',
  productionCreate: 'production.create',
  productionUpdate: 'production.update',
  productionRelease: 'production.release',
  productionComplete: 'production.complete',
  productionCancel: 'production.cancel',

  requisitionsView: 'requisitions.view',
  requisitionsCreate: 'requisitions.create',
  requisitionsSubmit: 'requisitions.submit',
  requisitionsApprove: 'requisitions.approve',
  requisitionsFulfil: 'requisitions.fulfil',
  requisitionsCancel: 'requisitions.cancel',

  replenishmentView: 'replenishment.view',
  replenishmentManage: 'replenishment.manage',
  inventoryAdjust: 'inventory.adjust',
} as const;

// ---------------------------------------------------------------------------
// shapes
// ---------------------------------------------------------------------------

export interface WarehouseRef {
  id: string;
  name: string;
  code: string;
  branchId?: string | null;
}

export interface StockTransferLine {
  id: string;
  inventoryItemId: string;
  unitId: string;
  /** Dispatched, in the item's own stock unit — what left the commissary. */
  quantity: string;
  /** Null while still on the road: "not yet received" and "received none" are different facts. */
  receivedQuantity: string | null;
  /** `receivedQuantity − quantity`, written down rather than recomputed. */
  discrepancyQuantity: string | null;
  /** Stamped at DISPATCH; the receive leg reuses it verbatim (ADR-0015 §5). */
  unitCost: string;
  notes: string | null;
  inventoryItem?: { id: string; name: string; sku?: string | null };
  unit?: { id: string; code: string };
}

export interface StockTransfer {
  id: string;
  tenantId: string;
  number: string;
  status: StockTransferStatus;
  fromWarehouseId: string;
  toWarehouseId: string;
  requisitionId: string | null;
  dispatchedAt: string | null;
  dispatchedById: string | null;
  receivedAt: string | null;
  receivedById: string | null;
  resolvedAt: string | null;
  resolvedById: string | null;
  resolution: TransferDiscrepancyResolution | null;
  resolutionNotes: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  fromWarehouse?: WarehouseRef;
  toWarehouse?: WarehouseRef;
  requisition?: { id: string; number: string; status: string } | null;
  lines?: StockTransferLine[];
  _count?: { lines: number };
}

/** `GET /stock-transfers/in-transit` — stock on the road, decomposed by the transfer holding it. */
export interface InTransitRow {
  transferId: string;
  transferNumber: string;
  toWarehouseId: string;
  toWarehouse?: WarehouseRef;
  inventoryItemId: string;
  inventoryItem?: { id: string; name: string; sku?: string | null };
  quantity: string;
  unitCost: string;
  dispatchedAt: string | null;
}

export interface ProductionOrderLine {
  id: string;
  inventoryItemId: string;
  plannedQuantity: string;
  actualQuantity: string | null;
  unitCost: string | null;
  inventoryItem?: { id: string; name: string; sku?: string | null };
}

export interface ProductionOrder {
  id: string;
  number: string;
  status: ProductionOrderStatus;
  recipeId: string;
  recipeVersionId: string | null;
  sourceWarehouseId: string;
  outputWarehouseId: string;
  outputInventoryItemId: string | null;
  plannedQuantity: string;
  producedQuantity: string | null;
  wastedQuantity: string | null;
  branchId: string | null;
  scheduledFor: string | null;
  notes: string | null;
  createdAt: string;
  recipe?: { id: string; name: string };
  outputItem?: { id: string; name: string; sku?: string | null };
  sourceWarehouse?: WarehouseRef;
  outputWarehouse?: WarehouseRef;
  lines?: ProductionOrderLine[];
  _count?: { lines: number };
}

export interface RequisitionLine {
  id: string;
  inventoryItemId: string;
  requestedQuantity: string;
  approvedQuantity: string | null;
  unitId: string;
  notes: string | null;
  inventoryItem?: { id: string; name: string; sku?: string | null };
}

export interface BranchRequisition {
  id: string;
  number: string;
  status: BranchRequisitionStatus;
  branchId: string;
  destinationWarehouseId: string | null;
  sourceWarehouseId: string | null;
  neededBy: string | null;
  notes: string | null;
  createdAt: string;
  branch?: { id: string; name: string; code?: string | null };
  destinationWarehouse?: WarehouseRef;
  sourceWarehouse?: WarehouseRef;
  lines?: RequisitionLine[];
  transfers?: { id: string; number: string; status: string; dispatchedAt: string | null; receivedAt: string | null }[];
  _count?: { lines: number; transfers: number };
}

export interface ReplenishmentPar {
  id: string;
  warehouseId: string;
  inventoryItemId: string;
  parLevel: string;
  reorderPoint: string | null;
  isActive: boolean;
  warehouse?: WarehouseRef;
  inventoryItem?: { id: string; name: string; sku?: string | null };
}

export interface ReplenishmentPlanRow {
  warehouseId: string;
  inventoryItemId: string;
  parLevel: string;
  onHand: string;
  inTransit: string;
  shortfall: string;
  inventoryItem?: { id: string; name: string; sku?: string | null };
}

export interface DemandRow {
  inventoryItemId: string;
  inventoryItem?: { id: string; name: string; sku?: string | null };
  requiredQuantity: string;
  sourceOnHand?: string | null;
}

// ---------------------------------------------------------------------------
// request bodies
// ---------------------------------------------------------------------------

export interface StockTransferLineBody {
  inventoryItemId: string;
  quantity: string;
  unitId?: string;
  notes?: string;
}

export interface CreateStockTransferBody {
  fromWarehouseId: string;
  toWarehouseId: string;
  requisitionId?: string;
  notes?: string;
  lines: StockTransferLineBody[];
}

export interface ReceiveStockTransferLineBody {
  inventoryItemId: string;
  receivedQuantity: string;
  unitId?: string;
  notes?: string;
}

export interface ReceiveStockTransferBody {
  lines?: ReceiveStockTransferLineBody[];
  occurredAt?: string;
  notes?: string;
  operationId?: string;
}

export interface ResolveStockTransferBody {
  resolution: TransferDiscrepancyResolution;
  notes: string;
  occurredAt?: string;
  operationId?: string;
}

export interface CreateProductionOrderBody {
  recipeId: string;
  recipeVersionId?: string;
  sourceWarehouseId: string;
  outputWarehouseId?: string;
  outputInventoryItemId?: string;
  plannedQuantity: string;
  branchId?: string;
  scheduledFor?: string;
  notes?: string;
}

export interface CompleteProductionOrderBody {
  producedQuantity: string;
  wastedQuantity?: string;
  lines?: { inventoryItemId: string; quantity: string }[];
  occurredAt?: string;
  notes?: string;
  operationId?: string;
}

export interface CreateRequisitionBody {
  branchId: string;
  destinationWarehouseId?: string;
  neededBy?: string;
  notes?: string;
  lines: { inventoryItemId: string; requestedQuantity: string; unitId?: string; notes?: string }[];
}

export interface ApproveRequisitionBody {
  sourceWarehouseId: string;
  lines?: { inventoryItemId: string; approvedQuantity: string }[];
  reason?: string;
}

export interface UpsertParBody {
  warehouseId: string;
  inventoryItemId: string;
  parLevel: string;
  reorderPoint?: string;
  isActive?: boolean;
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

export function apiMessage(error: unknown, fallback: string): string {
  const payload = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  if (Array.isArray(payload)) return payload.filter(Boolean).join(' · ');
  if (typeof payload === 'string' && payload.trim()) return payload;
  return fallback;
}

export function newOperationId(prefix: string): string {
  const random =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return `dashboard:${prefix}:${random}`;
}
